/*************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2018 Adobe
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 **************************************************************************/
export const pageViewModifiers = Object.freeze({
  NO_MOD: 0,
  FIT_PAGE: 1,
  FIT_WIDTH: 2,
  FIXED_WIDTH: 3,
  FIT_HEIGHT: 4
});

const statusObj = Object.freeze({
  HAS_CONTENT_CALL_TO_BE_MADE: -1,
  NOT_PRESENT: 0,
  PRESENT_AND_NOT_FETCHED: 1,
  PRESENT_AND_FETCHING: 2,
  PRESENT_AND_FETCHED: 3
});

export const bookmarksStatuses = statusObj;
export const attachmentsStatus = statusObj;
export const digSigStatus = statusObj;

export const DIGSIG_VALIDATE_BUTTON_STATE = Object.freeze({
  DISABLE: 0,
  ENABLE: 1,
  HIDDEN: 2
});

export const lhpFeatures = Object.freeze({
  NONE: 0,
  THUMBNAIL: 1,
  BOOKMARKS: 2,
  ATTACHMENTS: 3
});

export const STATUS = Object.freeze({
  FALSE: false,
  TRUE: true,
  UNDEFINED: undefined
});

export const hudUiControlAlignments = Object.freeze({
  LEFT: "LEFT",
  RIGHT: "RIGHT",
  CENTER: "CENTER",
  NONE: "NONE"
});

export const animationType = Object.freeze({
  FADE: "FADE",
  SLIDE: "SLIDE"
});

export const loadFailureType = Object.freeze({
  FILE_SIZE: "File Size is zero",
  CORRUPT_PDF: "File is corrupted",
  UNSUPPORTED_FEATURES_DIALOG: "Some features might not work"
});

export const securityType = Object.freeze({
  PASSWORD_PROTECTED: "Standard: Password Required",
  NONE: "None"
});

export const PERMISSION_LEVEL = Object.freeze({
  DOC: "doc",
  ANNOT: "annot",
  FORM: "form"
});

export const OPERATIONS_BIT_MAP = Object.freeze({
  NONE: 0b0000000,
  CREATE: 0b0000001,
  DELETE: 0b0000010,
  MODIFY: 0b0000100,
  COPY: 0b0001000,
  PRINT: 0b0110000,
  PRINT_HIGH: 0b0010000,
  PRINT_LOW: 0b0100000,
  FILL_IN: 0b1000000
});

export const appIdentifiers = Object.freeze({
  FIT_PAGE: "FIT_PAGE",
  FIT_WIDTH: "FIT_WIDTH",
  FIXED_WIDTH: "FIXED_WIDTH",
  SINGLE_PAGE: "SINGLE_PAGE",
  PAGE_BY_PAGE: "PAGE_BY_PAGE",
  CONTINUOUS: "CONTINUOUS",
  IN_PLACE: "IN_PLACE",
  PAGE_NAVIGATION: "PAGE_NAVIGATION_CONTROL",
  FILE_NAME_TOAST: "FILE_NAME_TOAST",
  PAGE_NUMBER: "PAGE_NUMBER_CONTROL",
  PAGE_VIEW_SWITCHER: "PAGE_VIEW_SWITCHER",
  ZOOM_CONTROL: "ZOOM_CONTROL",
  PRINT: "PRINT",
  DV_SWITCH: "LIQUID_MODE_SWITCH",
  DOWNLOAD: "DOWNLOAD",
  ROTATE_VIEW_CONTROL: "ROTATE_VIEW_CONTROL",
  DOCK: "DOCK_SWITCH",
  THUMBNAILS: "THUMBNAILS",
  BOOKMARKS: "BOOKMARKS",
  ATTACHMENTS: "ATTACHMENTS",
  BRANDING_ICON: "BRANDING_ICON",
  SEARCH: "SEARCH",
  SCRUBBER: "SCRUBBER",
  UNSUPPORTED_FEATURE_ALERT: "UNSUPPORTED_FEATURE_ALERT",
  TWO_COLUMN: "TWO_COLUMN",
  TWO_COLUMN_FIT_PAGE: "TWO_COLUMN_FIT_PAGE",
  TWO_COLUMN_FIT_WIDTH: "TWO_COLUMN_FIT_WIDTH",
  FORCE_SHOW_COMMENTING_TOOLS: "FORCE_SHOW_COMMENTING_TOOLS",
  FULL_SCREEN: "FULL_SCREEN",
  CLASSIC_VIEW: "CLASSIC_VIEW",
  FIRST_TIME_ENTER_EDIT: "FIRST_TIME_ENTER_EDIT",
  OPEN_IN_DESKTOP_FOR_UNSUPPORTED_FEATURE: "OPEN_IN_DESKTOP_FOR_UNSUPPORTED_FEATURE",
  FIT_ONE_FULL_PAGE: "FIT_ONE_FULL_PAGE",
  DOCUMENT_PERMISSIONS_TOAST: "DOCUMENT_PERMISSIONS_TOAST",
  VIEW_DOCUMENT_PROPERTIES: "VIEW_DOCUMENT_PROPERTIES"
});

export const allowedCustomizablePanels = Object.freeze({
  HUD: 0,
  LHP: 1,
  TOP_BAR: 2,
  DOCUMENT: 3,
  PAGED_VIEW_STATE: 4,
  QUICK_TOOLS: 5,
  CONTEXT_MENU: 6,
  SKINNY_RAIL: 7
});

export const docViewModes = Object.freeze({
  UNKNOWN: 0,
  CLASSIC_VIEW: 1,
  NON_PDF: 2,
  NO_PREVIEW: 3,
  SINGLE_PAGE: 4,
  IN_PLACE: 5,
  PAGE_BY_PAGE: 6,
  CONTINUOUS: 7,
  LIQUID_MODE_VIEW: 8,
  TWO_COLUMN: 9,
  TWO_COLUMN_FIT_PAGE: 10,
  TWO_COLUMN_FIT_WIDTH: 11,
  FULL_SCREEN: 12,
  FIT_ONE_FULL_PAGE: 13
});

export const errorCodes = Object.freeze({
  NO_ERROR: 0,
  INCORRECT_PASSWORD: 3
});

export const loadStatus = Object.freeze({
  NOT_LOADED: 0,
  BEING_LOADED: 1,
  LOADED: 2,
  BEING_PRELOADED: 3,
  PINCH_ZOOM_IN_PROGRESS: 4,
  FAILED: 5,
  UNLOADED: 6
});

export const dcapiMetadataFailure = Object.freeze({
  NUM_PAGES_UNDEFINED: "numPages not received in metadata",
  DOC_TYPE_UNDEFINED: "document type not received in metadata",
  PROMISE_REJECTED: "promise rejected"
});

export const responseSource = Object.freeze({
  NONE: 0,
  DC: 1,
  AJS: 2
});

export const httpErrorCodes = Object.freeze({
  BAD_REQUEST: 400,
  UNAUTHORIZED: 401,
  FORBIDDEN: 403,
  NOT_FOUND: 404,
  NOT_ACCEPTABLE: 406,
  UNSUPPORTED_MEDIA_TYPE: 415,
  TOO_MANY_REQUEST: 429,
  INTERNAL_SERVER_ERROR: 500,
  BAD_GATEWAY: 502,
  SERVICE_UNAVAILABLE: 503,
  GATEWAY_TIMEOUT: 504
});

// Do not use any standard HTTP error codes as values here.
export const noPreviewErrorCodes = Object.freeze({
  NO_ERROR: 0,
  PASSWORD_CANCELLED: 1,
  CORRUPT_PDF: 2
});

export const SecondaryMetadataStatus = Object.freeze({
  SUCCEEDED: "succeeded",
  FAILED: "failed",
  UNKNOWN: "unknown"
});

export const AJSMetadataStatus = Object.freeze({
  SUCCEEDED: "succeeded",
  FAILED: "failed",
  UNKNOWN: "unknown"
});

export const AnnotType = Object.freeze({
  TEXT: 0x00000001,
  LINK: 0x00000002,
  FREE_TEXT: 0x00000004,
  LINE: 0x00000008,
  SQUARE: 0x00000010,
  CIRCLE: 0x00000020,
  POLYGON: 0x00000040,
  POLYLINE: 0x00000080,
  HIGHLIGHT: 0x00000100,
  UNDERLINE: 0x00000200,
  SQUIGGLY: 0x00000400,
  STRIKEOUT: 0x00000800,
  CARET: 0x00001000,
  STAMP: 0x00002000,
  INK: 0x00004000,
  POPUP: 0x00008000,
  FILE_ATTACHMENT: 0x00010000,
  SOUND: 0x00020000,
  MOVIE: 0x00040000,
  SCREEN: 0x00080000,
  WIDGET: 0x00100000,
  WATERMARK: 0x00200000,
  THREE_D: 0x00400000,
  FREE_TEXT_TYPEWRITER: 0x00800000,
  ALL: 0xffffffff
});

export const featureLifecyleHooks = Object.freeze({
  BEFORE_PRINT: "beforePrint",
  BEFORE_ROTATE_VIEW: "beforeRotateView",
  AFTER_PRINT: "afterPrint",
  BEFORE_DOWNLOAD: "beforeDownload",
  AFTER_DOWNLOAD: "afterDownload",
  PRINT_COMPLETED: "printCompleted",
  BEFORE_QUICK_TOOL_ACTIVATED: "beforeQuickToolActivated",
  SAVE_BEFORE_QUICK_TOOL_ACTIVATED: "saveBeforeQuickToolActivated",
  BEFORE_NAVIGATION_BAR_TOOL_ACTIVATED: "beforeNavigationBarToolActivated",
  SAVE_BEFORE_NAVIGATION_BAR_TOOL_ACTIVATED: "saveBeforeNavigationBarToolActivated"
});

export const printAnalyticsEvents = Object.freeze({
  PRINT_IFRAME_LOADED: "printIframeLoaded",
  PRINT_IFRAME_NOT_LOADED: "printIframeNotLoaded",
  PRINT_CONTENT_DOCUMENT_NOT_LOADED: "printContentDocumentNotLoadedInIFrame",
  PRINT_WINDOW_BEFORE_EVENT: "printWindowBeforeEvent",
  PRINT_WINDOW_AFTER_EVENT: "printWindowAfterEvent"
});

export const rhpDropinIds = Object.freeze({
  EDIT_GENAI_PANEL: "verb-edit-chat-helper",
  COMMENTS: "comments",
  BOOKMARKS: "bookmarks",
  THUMBNAILS: "thumbnails",
  ATTACHMENTS: "attachments",
  LM_SETTINGS: "lm-settings",
  DIGITAL_SIGNATURES: "signatures",
  PERMISSION_DETAILS: "permissions"
});
export const rhpDropinIdsForCommenting = [rhpDropinIds.COMMENTS];

export const VERB_COMMENT_STICKY_NOTE = "verb-comment-stickynote";
export const VERB_COMMENT_FTE = "comments-fte";
export const VERB_COMMENT_HIGHLIGHT = "verb-comment-highlight";
export const VERB_COMMENT_TEXT = "verb-comment-text";
export const VERB_COMMENT_STRIKEOUT = "verb-comment-strikeout";
export const VERB_COMMENT_UNDERLINE = "verb-comment-underline";
export const VERB_COMMENT_DRAW = "verb-comment-draw";
export const VERB_FILLSIGN_TEXT = "verb-fill-sign-qt-text";
export const VERB_FILLSIGN_SIGNATURE = "verb-fill-sign-qt-signature";
export const VERB_COPY_TEXT = "verb-copy-text";
export const VERB_OPEN_LINK = "verb-open-link";
export const VERB_COPY_LINK = "verb-copy-link";
export const VERB_FILLSIGN_CHECK = "verb-fill-sign-qt-check";
export const VERB_FILLSIGN_CROSS = "verb-fill-sign-qt-cross";
export const VERB_FILLSIGN_DOT = "verb-fill-sign-qt-dot";
export const VERB_FILLSIGN_CIRCLE = "verb-fill-sign-qt-circle";
export const VERB_FILLSIGN_LINE = "verb-fill-sign-qt-line";
export const VERB_PREVIEW_SELECT = "preview-select";
export const VERB_PREVIEW_PAN = "preview-pan";
export const VERB_FULL_SCREEN = "verb-full-screen";
export const VERB_LIQUID_MODE = "verb-liquid-mode";
export const VERB_READ_ALOUD = "verb-read-aloud";
export const VERB_QANDA = "verb-qanda";
export const OUTLINE = "verb-outline";

export const quickToolsDropinIds = Object.freeze({
  SELECT: "preview-select",
  COMMENT: VERB_COMMENT_STICKY_NOTE,
  HIGHLIGHT: VERB_COMMENT_HIGHLIGHT,
  DRAW: VERB_COMMENT_DRAW,
  FILLSIGN_TEXT: VERB_FILLSIGN_TEXT,
  FILLSIGN_SIGNATURE: VERB_FILLSIGN_SIGNATURE
});

export const initialQTOrderedVerbs = [
  VERB_PREVIEW_SELECT,
  VERB_COMMENT_STICKY_NOTE,
  VERB_COMMENT_HIGHLIGHT,
  VERB_COMMENT_DRAW,
  VERB_FILLSIGN_TEXT,
  VERB_FILLSIGN_SIGNATURE
];

export const quickToolsDropinIdsForCommenting = [VERB_COMMENT_STICKY_NOTE, VERB_COMMENT_HIGHLIGHT, VERB_COMMENT_DRAW];

export const quickToolsDropinIdsForFillSign = [VERB_FILLSIGN_TEXT, VERB_FILLSIGN_SIGNATURE];

export const quickToolsVerbIdsForMarkupAnnots = [
  VERB_COMMENT_HIGHLIGHT,
  VERB_COMMENT_STRIKEOUT,
  VERB_COMMENT_UNDERLINE,
  VERB_COMMENT_STICKY_NOTE
];

export const quickToolsVerbIdsForCommenting = [
  VERB_COMMENT_STICKY_NOTE,
  VERB_COMMENT_TEXT,
  VERB_COMMENT_HIGHLIGHT,
  VERB_COMMENT_STRIKEOUT,
  VERB_COMMENT_UNDERLINE,
  VERB_COMMENT_DRAW
];

export const quickToolsPanels = Object.freeze({
  PRIMARY: "primary",
  SECONDARY: "secondary"
});

export const dropinNames = Object.freeze({
  SKINNY_RAIL: "skinny-rail",
  COMMENTS: rhpDropinIds.COMMENTS,
  BOOKMARKS: rhpDropinIds.BOOKMARKS,
  THUMBNAILS: rhpDropinIds.THUMBNAILS,
  ATTACHMENTS: rhpDropinIds.ATTACHMENTS,
  DIGITAL_SIGNATURES: rhpDropinIds.DIGITAL_SIGNATURES,
  PERMISSION_DETAILS: rhpDropinIds.PERMISSION_DETAILS,
  TOOLS_BOARD: "tools-board"
});

export const modernViewerDropinContext = Object.freeze({
  TOOLS_BOARD: "ModernViewerToolsBoard",
  SKINNY_RAIL: "ModernViewerSkinnyRail",
  QUICK_TOOLS: "ModernViewerQuickTools",
  DEEP_LINK: "ModernViewerDeepLink",
  // TO DO update it to ModernViewerContextMenu and in respective dropins
  CONTEXT_MENU: "ViewerContextMenu",
  MODERN_MOBILE_CONTEXT_MENU: "ModernViewerMobileContextMenu",
  MODERN_MOBILE_OVERFLOW_MENU: "MobileModernViewerOverflowMenu",
  STICKY_TOOLS: "ModernViewerStickyTools",
  REDIRECT_TO_APP: "RedirectToApp",
  HOME_BANNER_CAROUSEL: "homeBannerCarousel",
  WHATS_NEW: "WhatsNew",
  WAITLIST_APPROVED_DIALOG: "waitlistApprovedDialog",
  TOOLS2: "Tools2",
  PDFNOW_LIFECYCLE: "PDFNowLifeCycle",
  RECENT_FILES_CONTEXT_MENU: "MoreMenu",
  RECENT_FILES_CONTEXT_MENU_REVIEW: "MoreMenuReview",
  DOCUMENTS_CONTEXT_MENU_REVIEW: "MoreMenuDocumentsReview",
  ALL_TOOLS: "AllTools"
});

export const CLASSIC_VIEW_OFFICE_PREVIEW_CONTEXT = "OfficePreview";

export const GENAI_NON_PDF_CONVERT_VIEWER_WORKFLOW = "NonPDFConvertViewerWorkflow";

// Place ordered verbIds here based on appearance of context menu items
export const orderedContextMenuVerbIds = Object.freeze({
  VERB_COMMENT_COLOR_PICKER: "verb-comment-color-picker",
  VERB_COMMENT_STROKE_WIDTH: "verb-comment-stroke-width",
  VERB_COMMENT_DELETE: "verb-comment-delete",
  VERB_COPY_LINK: "verb-copy-link",
  VERB_OPEN_LINK: "verb-open-link",
  VERB_COMMENT_STICKYNOTE: "verb-comment-stickynote",
  VERB_COMMENT_HIGHLIGHT: "verb-comment-highlight",
  VERB_COMMENT_UNDERLINE: "verb-comment-underline",
  VERB_COMMENT_STRIKEOUT: "verb-comment-strikeout",
  VERB_COMMENT_DRAW: "verb-comment-draw",
  VERB_EDIT_PDF: "verb-edit-pdf",
  VERB_CROP_PAGES_VIEWER: "verb-crop-pages-viewer",
  VERB_COPY_TEXT: "verb-copy-text",
  VERB_EDIT_PDF_VIEWER: "verb-edit-pdf-viewer",
  VERB_EDIT_FORMAT_TEXT: "verb-edit-format-text",
  VERB_SENDFORSIGNATURE: "verb-sendforsignature",
  AI_SECTION_VERB: "verb-ai-section",
  VERB_GENAI_QANDA: VERB_QANDA,
  VERB_GENAI_SUMMARY: OUTLINE
});

export const contextMenuPlacements = Object.freeze({
  CONTEXT_MENU_HEADER: "CONTEXT_MENU_HEADER",
  CONTEXT_MENU_BODY: "CONTEXT_MENU_BODY"
});

export const cursorType = Object.freeze({
  DEFAULT: "default",
  GRAB: "grab",
  GRABBING: "grabbing",
  POINTER: "pointer",
  TEXT: "text"
});

export const liquidModeViewState = Object.freeze({
  DEFAULT: 0,
  LOADING: 1,
  READY: 2,
  LOADED: 3,
  LOADING_FAILED: 4,
  EXITED: 5
});

export const liquidModeNetworkProxyTechnology = Object.freeze({
  NONE: 0,
  SERVICE_WORKERS: 1
});

export const liquidModeError = Object.freeze({
  ADOBDE_SENSEI_ERROR: "AdobeSenseiPredictRequestFailed",
  PREPIPELINE_DISQUALIFICATION: "PrepipelineDisqualification",
  UNKNOWN_ERROR: "UnknownError"
});

export const liquidModeDisqualificationSeverity = Object.freeze({
  NONE: 0,
  SOFT: 1,
  HARD: 2,
  UNKNOWN: 3
});

export const actionTags = Object.freeze({
  LOCAL: "local",
  HYBRID: "hybrid"
});

export const pageOrientation = Object.freeze({
  NONE: "NONE",
  LANDSCAPE: "LANDSCAPE",
  PORTRAIT: "PORTRAIT"
});

export const fullScreenExitReason = Object.freeze({
  DIALOG: "DIALOG",
  BROWSER_ALERT: "BROWSER_ALERT",
  ESCAPE: "ESCAPE",
  CROSS_ICON: "CROSS_ICON",
  KEYBOARD_SHORTCUT: "KEYBOARD_SHORTCUT",
  PUBLIC_API: "PUBLIC_API"
});

export const supportedFileExtensions = Object.freeze({
  // add more supported files extensions to the list
  PDF: "pdf",
  AI: "ai",
  BMP: "bmp",
  DOC: "doc",
  DOCX: "docx",
  FORM: "form",
  GIF: "gif",
  INDD: "indd",
  JPEG: "jpeg",
  JPG: "jpg",
  PNG: "png",
  PPT: "ppt",
  PPTX: "pptx",
  PSD: "psd",
  RTF: "rtf",
  TEXT: "text",
  TIF: "tif",
  TIFF: "tiff",
  TXT: "txt",
  XLS: "xls",
  XLSX: "xlsx"
});

export const hcThemeColors = Object.freeze({
  TEXT: "text",
  BACKGROUND: "background",
  BUTTON_TEXT: "buttonText",
  BUTTON_FACE: "buttonFace",
  HIGHLIGHT: "highlight",
  HIGHLIGHT_TEXT: "highlightText",
  LINK_TEXT: "linkText",
  DISABLED_TEXT: "disabledText"
});

export const thumbnailOrientationState = Object.freeze({
  PORTRAIT: 0,
  LANDSCAPE: 1
});

export const customizableComponentParent = Object.freeze({
  FIT_PAGE: [allowedCustomizablePanels.HUD],
  FIT_WIDTH: [allowedCustomizablePanels.HUD],
  PAGE_NAVIGATION_CONTROL: [allowedCustomizablePanels.HUD],
  PAGE_NUMBER_CONTROL: [allowedCustomizablePanels.HUD],
  PAGE_VIEW_SWITCHER: [allowedCustomizablePanels.HUD],
  ZOOM_CONTROL: [allowedCustomizablePanels.HUD],
  ROTATE_VIEW_CONTROL: [allowedCustomizablePanels.HUD],
  TWO_COLUMN: [allowedCustomizablePanels.HUD],
  DOCK_SWITCH: [allowedCustomizablePanels.HUD],
  DOWNLOAD: [allowedCustomizablePanels.HUD],
  PRINT: [allowedCustomizablePanels.HUD],
  FILE_NAME_TOAST: [allowedCustomizablePanels.DOCUMENT],
  UNSUPPORTED_FEATURE_ALERT: [allowedCustomizablePanels.DOCUMENT],
  SEARCH: [allowedCustomizablePanels.TOP_BAR],
  SCRUBBER: [allowedCustomizablePanels.PAGED_VIEW_STATE],
  PREVIEW_SELECT_CONTROL: [allowedCustomizablePanels.QUICK_TOOLS],
  PREVIEW_PAN_CONTROL: [allowedCustomizablePanels.QUICK_TOOLS],
  COMMENT_STICKYNOTE_CONTROL: [allowedCustomizablePanels.QUICK_TOOLS, allowedCustomizablePanels.CONTEXT_MENU],
  COMMENT_TEXT_CONTROL: [allowedCustomizablePanels.QUICK_TOOLS, allowedCustomizablePanels.CONTEXT_MENU],
  COMMENT_HIGHLIGHT_CONTROL: [allowedCustomizablePanels.QUICK_TOOLS, allowedCustomizablePanels.CONTEXT_MENU],
  COMMENT_UNDERLINE_CONTROL: [allowedCustomizablePanels.QUICK_TOOLS, allowedCustomizablePanels.CONTEXT_MENU],
  COMMENT_STRIKEOUT_CONTROL: [allowedCustomizablePanels.QUICK_TOOLS, allowedCustomizablePanels.CONTEXT_MENU],
  COMMENT_DRAW_CONTROL: [allowedCustomizablePanels.QUICK_TOOLS, allowedCustomizablePanels.CONTEXT_MENU],
  FILLSIGN_TEXT_QT_CONTROL: [allowedCustomizablePanels.QUICK_TOOLS],
  FILLSIGN_CHECK_QT_CONTROL: [allowedCustomizablePanels.QUICK_TOOLS],
  FILLSIGN_CROSS_QT_CONTROL: [allowedCustomizablePanels.QUICK_TOOLS],
  FILLSIGN_DOT_QT_CONTROL: [allowedCustomizablePanels.QUICK_TOOLS],
  FILLSIGN_CIRCLE_QT_CONTROL: [allowedCustomizablePanels.QUICK_TOOLS],
  FILLSIGN_LINE_QT_CONTROL: [allowedCustomizablePanels.QUICK_TOOLS],
  FILLSIGN_SIGNATURE_QT_CONTROL: [allowedCustomizablePanels.QUICK_TOOLS],
  FORCE_SHOW_COMMENTING_TOOLS: [allowedCustomizablePanels.DOCUMENT],
  COMMENTS_PANEL: [allowedCustomizablePanels.SKINNY_RAIL],
  FIRST_TIME_ENTER_EDIT: [allowedCustomizablePanels.DOCUMENT],
  OPEN_IN_DESKTOP_FOR_UNSUPPORTED_FEATURE: [allowedCustomizablePanels.DOCUMENT],
  FULL_SCREEN: [allowedCustomizablePanels.HUD],
  DOCUMENT_PERMISSIONS_TOAST: [allowedCustomizablePanels.DOCUMENT],
  VIEW_DOCUMENT_PROPERTIES: [allowedCustomizablePanels.DOCUMENT]
});

export const VerbIdProps = Object.freeze({
  PREVIEW_SELECT_CONTROL: VERB_PREVIEW_SELECT,
  PREVIEW_PAN_CONTROL: VERB_PREVIEW_PAN,
  COMMENT_STICKYNOTE_CONTROL: VERB_COMMENT_STICKY_NOTE,
  COMMENT_TEXT_CONTROL: VERB_COMMENT_TEXT,
  COMMENT_HIGHLIGHT_CONTROL: VERB_COMMENT_HIGHLIGHT,
  COMMENT_UNDERLINE_CONTROL: VERB_COMMENT_UNDERLINE,
  COMMENT_STRIKEOUT_CONTROL: VERB_COMMENT_STRIKEOUT,
  COMMENT_DRAW_CONTROL: VERB_COMMENT_DRAW,
  FILLSIGN_TEXT_QT_CONTROL: VERB_FILLSIGN_TEXT,
  FILLSIGN_CHECK_QT_CONTROL: VERB_FILLSIGN_CHECK,
  FILLSIGN_CROSS_QT_CONTROL: VERB_FILLSIGN_CROSS,
  FILLSIGN_DOT_QT_CONTROL: VERB_FILLSIGN_DOT,
  FILLSIGN_CIRCLE_QT_CONTROL: VERB_FILLSIGN_CIRCLE,
  FILLSIGN_LINE_QT_CONTROL: VERB_FILLSIGN_LINE,
  FILLSIGN_SIGNATURE_QT_CONTROL: VERB_FILLSIGN_SIGNATURE
});

export const UIControlToSkinnyRailVerbsMapping = Object.freeze({
  COMMENTS_PANEL: rhpDropinIds.COMMENTS
});

export const EVENT_NAMES = Object.freeze({
  OPEN: "Open",
  CLOSE: "Close",
  MOUSE_UP: "Mouse Up",
  WILL_CLOSE: "Close",
  WILL_PRINT: "Will Print",
  DID_PRINT: "Did Print",
  WILL_SAVE: "Will Save",
  DID_SAVE: "Did Save"
});

export const PDF_ACTIONS = Object.freeze({
  GOTO: "GoTo",
  URI: "URI",
  HIDE: "Hide",
  LAUNCH: "Launch",
  THREAD: "Thread",
  SOUND: "Sound",
  MOVIE: "Movie",
  JAVASCRIPT: "JavaScript",
  RESET_FORM: "ResetForm",
  SUBMIT_FORM: "SubmitForm",
  IMPORT_DATA: "ImportData",
  NAMED: "Named",
  NAMED_ACTIONS: {
    NEXT_PAGE: "NextPage",
    PREV_PAGE: "PrevPage",
    FIRST_PAGE: "FirstPage",
    LAST_PAGE: "LastPage",
    SHOW_HIDE_THUMBNAILS: "ShowHideThumbnails",
    SHOW_HIDE_BOOKMARKS: "ShowHideBookmarks",
    SHOW_HIDE_FILE_ATTACHMENT: "ShowHideFileAttachment",
    SAVE_AS: "SaveAs",
    ZOOM_VIEW_IN: "ZoomViewIn",
    ZOOM_VIEW_OUT: "ZoomViewOut",
    FIT_WIDTH: "FitWidth",
    FIT_HEIGHT: "FitHeight",
    PRINT: "Print"
  }
});

export const APP_THEMES = Object.freeze({
  LIGHT: "light",
  DARK: "dark"
});

export const QUICK_TOOLS_STYLE = Object.freeze({
  INACTIVE: {
    backgroundColor: "var(--spectrum-global-color-gray-200)",
    borderColor: "var(--spectrum-global-color-gray-200)",
    color: "var(--spectrum-global-color-gray-500)"
  },
  LIGHT: {
    DEFAULT: {
      color: "var(--spectrum-global-color-gray-800)",
      background: "transparent"
    },
    HOVER: {
      color: "var(--spectrum-global-color-static-gray-900)",
      background: "var(--spectrum-global-color-static-gray-200)"
    },
    ACTIVE: {
      color: "var(--spectrum-global-color-static-gray-900)",
      background: "var(--spectrum-global-color-gray-200)"
    },
    SELECTED: {
      color: "var(--spectrum-global-color-gray-50)",
      background: "#0072ee"
    }
  },
  DARK: {
    DEFAULT: {
      color: "var(--spectrum-global-color-gray-700)",
      background: "transparent"
    },
    HOVER: {
      color: "var(--spectrum-global-color-gray-900)",
      background: "var(--spectrum-global-color-gray-300)"
    },
    ACTIVE: {
      color: "var(--spectrum-global-color-gray-900)",
      background: "#095ABA"
    },
    SELECTED: {
      color: "var(--spectrum-global-color-gray-900)",
      background: "var(--spectrum-global-color-blue-400)"
    }
  }
});

export const VERB_TO_VERB_EVENT_NAME = {
  "verb-edit-pdf": "edit-pdf",
  "group-edit": "edit-pdf"
};

export const KeyboardNavigationDirection = {
  RIGHT: 1,
  LEFT: 2,
  UP: 3,
  DOWN: 4
};
export const ACCESSIBILITY_FORM_NODE_TITLE = Object.freeze({
  TEXTBOX: "text field",
  PASSWORD: "Password",
  EMPTY_VALUE: "empty",
  COMBO_BOX: "combobox",
  READONLY: "read only",
  REQUIRED: "required",
  BUTTON: "button",
  CHECKBOX: "tickbox",
  CHECKBOX_SELECTED: "ticked",
  CHECKBOX_UNSELECTED: "unticked",
  RADIO_BUTTON: "radio button",
  RADIO_BUTTON_SELECTED: "selected",
  LISTBOX: "listbox",
  BARCODE: "barcode",
  SIGNATURE: "signature"
});

export const SKIP_PDF_NAMED_ACTIONS = Object.freeze([
  PDF_ACTIONS.NAMED_ACTIONS.NEXT_PAGE,
  PDF_ACTIONS.NAMED_ACTIONS.PREV_PAGE,
  PDF_ACTIONS.NAMED_ACTIONS.FIRST_PAGE,
  PDF_ACTIONS.NAMED_ACTIONS.LAST_PAGE,
  PDF_ACTIONS.NAMED_ACTIONS.ZOOM_VIEW_IN,
  PDF_ACTIONS.NAMED_ACTIONS.ZOOM_VIEW_OUT,
  PDF_ACTIONS.NAMED_ACTIONS.FIT_WIDTH,
  PDF_ACTIONS.NAMED_ACTIONS.FIT_HEIGHT
]);

export const toolsSupportingCaretMode = [
  "", // On initial load QT is empty but we need to show caret
  VERB_PREVIEW_SELECT,
  VERB_COMMENT_HIGHLIGHT,
  VERB_COMMENT_STRIKEOUT,
  VERB_COMMENT_UNDERLINE
];

export const printTriggerType = Object.freeze({
  PRINT_USING_HTML: "PrintUsingHTML",
  PRINT_USING_WINDOW: "PrintUsingWindow",
  PRINT_USING_IFRAME: "PrintUsingIframe"
});

export const fallbackPrintEvent = Object.freeze({
  PRINT_PDF_TAKING_LONGER_TIME: "PrintPDFTakingLongerTime",
  FALLBACK_PRINT_INITIATED: "FallbackPrintInitiated",
  RENDITIONS_COMPLETED: "FallbackPrintRenditionsCompleted"
});

export const printFailureType = Object.freeze({
  PRINT_PDF_NOT_LOADED_IN_IFRAME_ERROR: "PrintPDFNotLoadedInIframeError"
});

export const IFRAME_FEATURES = [
  "geolocation",
  "gamepad",
  "ch-ect",
  "midi",
  "display-capture",
  "usb",
  "local-fonts",
  "magnetometer",
  "picture-in-picture",
  "publickey-credentials-get",
  "window-placement",
  "accelerometer",
  "otp-credentials",
  "document-domain",
  "encrypted-media",
  "ch-save-data",
  "ch-ua-full-version-list",
  "ch-ua-wow64",
  "ch-downlink",
  "ch-ua-arch",
  "xr-spatial-tracking",
  "ch-prefers-color-scheme",
  "idle-detection",
  "ch-ua-platform-version",
  "ch-width",
  "sync-xhr",
  "ch-ua-model",
  "serial",
  "clipboard-read",
  "camera",
  "ch-viewport-width",
  "payment",
  "ch-viewport-height",
  "bluetooth",
  "ch-rtt",
  "ch-ua-full-version",
  "fullscreen",
  "autoplay",
  "cross-origin-isolated",
  "ch-dpr",
  "hid",
  "ch-ua-bitness",
  "keyboard-map",
  "ch-ua-platform",
  "screen-wake-lock",
  "gyroscope",
  "clipboard-write",
  "ch-ua-mobile",
  "ch-device-memory",
  "ch-ua",
  "microphone"
];

export const sequentialLoaderStates = Object.freeze({
  IDLE: 0,
  RUNNING: 1,
  PAUSED: 2
});

export const closeFlyoutReasons = Object.freeze({
  PAGE_CLICK: "pageclick"
});

export const reportURIEndPoints = Object.freeze({
  PROD_ENDPOINT: "https://dc-api.adobe.io/system/csp",
  STAGE_ENDPOINT: "https://dc-api-stage.adobe.io/system/csp",
  DEV_ENDPOINT: "https://dc-api-dev.adobe.io/system/csp"
});

export const TARGET_PRODUCTIVITY_CONTEXT_MENU = "productivity_contextMenu";
export const TARGET_PRODUCTIVITY_COMMENTS_PANEL = "productivity_commentPanel";
export const TARGET_PRODUCTIVITY_QUICK_ACTION = "productivity_quickAction";

export const FTESelectors = Object.freeze({
  [TARGET_PRODUCTIVITY_QUICK_ACTION]: VERB_COMMENT_HIGHLIGHT,
  [TARGET_PRODUCTIVITY_CONTEXT_MENU]: "CONTEXT_MENU_COACHMARK",
  [TARGET_PRODUCTIVITY_COMMENTS_PANEL]: "PAGE_LEVEL_COMMENT_CONTAINER"
});

/**
 * Parent Id of dom element inside which,
 * FTE CoackMark is rendered.
 */
export const FTE_COACHMARK_PARENT_ID = "FTE_COACHMARK_PARENT";
export const TARGET_BUILTIN_TOOLTIP = "builtIn";

export const documentPropertiesMenuItems = Object.freeze({
  DESCRIPTION: "DESCRIPTION",
  PERMISSIONS: "PERMISSIONS"
});

export const DOCUMENT_TYPE = Object.freeze({
  STANDARD: "Standard",
  ACROFORM: "Acroform",
  STATIC_XFA: "Static XFA",
  PORTFOLIO: "Portfolio"
});

export const docPropertiesDialogTriggerPoint = Object.freeze({
  TOAST: "toast",
  TOPBAR: "topbar",
  RHP: "RHP"
});

export const a11yClasses = Object.freeze({
  A11Y_COMMENT: "a11y-comment",
  A11Y_LINK: "a11y-link"
});

export const cacheBurstLogKeys = Object.freeze({
  DIFF_CONS_BURST: "diffBwConsecutiveCacheBurst",
  TIME_CACHE_FIX: "timeTakenInCacheFix"
});

export const pagesViewLoader = Object.freeze({
  CLASSIC_VIEW_CONTAINER: "classicViewContainer",
  THUMBNAILS_CONTAINER: "thumbnailContainer"
});

export const pageUsageStatus = Object.freeze({
  USED: "USED",
  NOT_USED: "NOT_USED"
});

export const outlineEvents = Object.freeze({
  DATA: 0,
  STREAM_ENDED: 1,
  UNZIP_STREAM_ERROR: 2,
  READABLE_STREAM_ERROR: 3,
  SENSEI_ERROR: 4,
  SENSEI_META_DATA: 5
});

export const ScrollDirection = Object.freeze({
  FORWARD: 1,
  BACKWARD: -1
});

export const optInStatus = Object.freeze({
  TRY_NOW: "TRY_NOW",
  OPT_IN_COMPLETE: "OPT_IN_COMPLETE",
  WAITLISTED: "WAITLISTED",
  DISABLED: "DISABLED"
});

export const BETA_BADGE_STYLE = Object.freeze({
  width: "27px",
  height: "14px",
  padding: "2px 4px 2px 5px"
});

export const RHP_VERBS_POSITION = Object.freeze({
  TOP: "top",
  BOTTOM: "bottom"
});

export const searchAddOnPanelTrigger = Object.freeze({
  ON_FOCUS: "ON_FOCUS",
  ALWAYS: "ALWAYS"
});
